// The full X12 DTM01 Date/Time Qualifier code list — shared by every DTM
// segment's qualifier element across the 850 and 852 dictionaries (and any
// future transaction set that reuses the DTM segment). Transcribed from the
// reference document "X12 Date and Time Qualifiers.txt"; footnote reference
// numbers from that document are not carried over, but every code, label and
// description it provides is.
//
// Each entry's `label` is the short name (used for the coded value's inline
// display); `description`, when the reference document provides a fuller
// explanation beyond the label (a second line under the code), carries that
// sentence too (see dictionary/types.ts's `CodeListEntry`). Most codes have a
// label only.

import type {CodeListEntry} from './types';

export const DATE_TIME_QUALIFIER_CODES: CodeListEntry[] = [
  {code: '001', label: 'Cancel After'},
  {code: '002', label: 'Delivery Requested'},
  {code: '003', label: 'Invoice'},
  {code: '004', label: 'Purchase Order'},
  {code: '005', label: 'Sailing'},
  {code: '006', label: 'Sold'},
  {code: '007', label: 'Effective'},
  {code: '008', label: 'Purchase Order Received'},
  {code: '009', label: 'Process'},
  {code: '010', label: 'Requested Ship'},
  {code: '011', label: 'Shipped'},
  {code: '012', label: 'Terms Discount Due'},
  {code: '013', label: 'Terms Net Due'},
  {code: '014', label: 'Deferred Payment'},
  {code: '015', label: 'Promotion Start'},
  {code: '016', label: 'Promotion End'},
  {code: '017', label: 'Estimated Delivery'},
  {code: '018', label: 'Available'},
  {code: '019', label: 'Unloaded'},
  {code: '020', label: 'Check'},
  {code: '021', label: 'Charge Back'},
  {code: '022', label: 'Freight Bill'},
  {code: '023', label: 'Promotion Order – Start'},
  {code: '024', label: 'Promotion Order – End'},
  {code: '025', label: 'Promotion Ship – Start'},
  {code: '026', label: 'Promotion Ship – End'},
  {code: '027', label: 'Promotion Requested Delivery – Start'},
  {code: '028', label: 'Promotion Requested Delivery – End'},
  {code: '029', label: 'Promotion Performance – Start'},
  {code: '030', label: 'Promotion Performance – End'},
  {code: '031', label: 'Promotion Invoice Performance – Start'},
  {code: '032', label: 'Promotion Invoice Performance – End'},
  {code: '033', label: 'Promotion Floor Stock Protect – Start'},
  {code: '034', label: 'Promotion Floor Stock Protect – End'},
  {code: '035', label: 'Delivered'},
  {code: '036', label: 'Expiration', description: 'Date coverage expires'},
  {code: '037', label: 'Ship Not Before'},
  {code: '038', label: 'Ship No Later'},
  {code: '039', label: 'Ship Week of'},
  {code: '040', label: 'Status (After and Including)'},
  {code: '041', label: 'Status (Prior and Including)'},
  {code: '042', label: 'Superseded'},
  {code: '043', label: 'Publication'},
  {code: '044', label: 'Settlement Date as Specified by the Originator'},
  {code: '045', label: 'Endorsement Date'},
  {
    code: '046',
    label: 'Field Failure',
    description: 'When a component causes a system to fail at a customer site',
  },
  {
    code: '047',
    label: 'Functional Test',
    description: 'When assembly was tested',
  },
  {
    code: '048',
    label: 'System Test',
    description: 'When the finished system was tested',
  },
  {
    code: '049',
    label: 'Prototype Test',
    description: 'When the prototype system or assembly was tested',
  },
  {code: '050', label: 'Received'},
  {code: '051', label: 'Cumulative Quantity Start'},
  {code: '052', label: 'Cumulative Quantity End'},
  {code: '053', label: 'Buyers Local'},
  {code: '054', label: 'Sellers Local'},
  {code: '055', label: 'Confirmed'},
  {code: '056', label: 'Estimated Port of Entry'},
  {code: '057', label: 'Actual Port of Entry'},
  {code: '058', label: 'Customs Clearance'},
  {code: '059', label: 'Inland Ship'},
  {code: '060', label: 'Engineering Change Level'},
  {code: '061', label: 'Cancel if Not Delivered by'},
  {code: '062', label: 'Blueprint'},
  {code: '063', label: 'Do Not Deliver After'},
  {code: '064', label: 'Do Not Deliver Before'},
  {code: '065', label: '1st Schedule Delivery'},
  {code: '066', label: '1st Schedule Ship'},
  {code: '067', label: 'Current Schedule Delivery'},
  {code: '068', label: 'Current Schedule Ship'},
  {code: '069', label: 'Promised for Delivery'},
  {code: '070', label: 'Scheduled for Delivery (After and Including)'},
  {code: '071', label: 'Requested for Delivery (After and Including)'},
  {code: '072', label: 'Promised for Delivery (After and Including)'},
  {code: '073', label: 'Scheduled for Delivery (Prior to and Including)'},
  {code: '074', label: 'Requested for Delivery (Prior to and Including)'},
  {code: '075', label: 'Promised for Delivery (Prior to and Including)'},
  {code: '076', label: 'Scheduled for Delivery (Week of)'},
  {code: '077', label: 'Requested for Delivery (Week of)'},
  {code: '078', label: 'Promised for Delivery (Week of)'},
  {code: '079', label: 'Promised for Shipment'},
  {code: '080', label: 'Scheduled for Shipment (After and Including)'},
  {code: '081', label: 'Requested for Shipment (After and Including)'},
  {code: '082', label: 'Promised for Shipment (After and Including)'},
  {code: '083', label: 'Scheduled for Shipment (Prior to and Including)'},
  {code: '084', label: 'Requested for Shipment (Prior to and Including)'},
  {code: '085', label: 'Promised for Shipment (Prior to and Including)'},
  {code: '086', label: 'Scheduled for Shipment (Week of)'},
  {code: '087', label: 'Requested for Shipment (Week of)'},
  {code: '088', label: 'Promised for Shipment (Week of)'},
  {code: '089', label: 'Inquiry'},
  {code: '090', label: 'Report Start'},
  {code: '091', label: 'Report End'},
  {code: '092', label: 'Contract Effective'},
  {code: '093', label: 'Contract Expiration'},
  {code: '094', label: 'Manufacture'},
  {code: '095', label: 'Bill of Lading'},
  {code: '096', label: 'Discharge'},
  {code: '097', label: 'Transaction Creation'},
  {code: '098', label: 'Bid (Effective)'},
  {code: '099', label: 'Bid Open (Date Bids Will Be Opened)'},
  {code: '100', label: 'No Shipping Schedule Established as of'},
  {code: '101', label: 'No Production Schedule Established as of'},
  {code: '102', label: 'Issue'},
  {code: '103', label: 'Award'},
  {code: '104', label: 'System Survey'},
  {code: '105', label: 'Quality Rating'},
  {code: '106', label: 'Required By'},
  {code: '107', label: 'Deposit'},
  {code: '108', label: 'Postmark'},
  {code: '109', label: 'Received at Lockbox'},
  {code: '110', label: 'Originally Scheduled Ship'},
  {code: '111', label: 'Manifest/Ship Notice'},
  {code: '112', label: 'Buyers Dock'},
  {code: '113', label: 'Sample Required'},
  {code: '114', label: 'Tooling Required'},
  {code: '115', label: 'Sample Available'},
  {code: '116', label: 'Scheduled Interchange Delivery'},
  {code: '118', label: 'Requested Pickup'},
  {code: '119', label: 'Test Performed'},
  {code: '120', label: 'Control Plan'},
  {code: '121', label: 'Feasibility Sign Off'},
  {code: '122', label: 'Failure Mode Effective'},
  {code: '124', label: 'Group Contract Effective'},
  {code: '125', label: 'Group Contract Expiration'},
  {code: '126', label: 'Wholesale Contract Effective'},
  {code: '127', label: 'Wholesale Contract Expiration'},
  {code: '128', label: 'Replacement Effective'},
  {code: '129', label: 'Customer Contract Effective'},
  {code: '130', label: 'Customer Contract Expiration'},
  {code: '131', label: 'Item Contract Effective'},
  {code: '132', label: 'Item Contract Expiration'},
  {code: '133', label: 'Accounts Receivable – Statement Date'},
  {code: '134', label: 'Ready for Inspection'},
  {code: '135', label: 'Booking'},
  {code: '136', label: 'Technical Rating'},
  {code: '137', label: 'Delivery Rating'},
  {code: '138', label: 'Commercial Rating'},
  {code: '139', label: 'Estimated'},
  {code: '140', label: 'Actual'},
  {
    code: '141',
    label: 'Assigned',
    description:
      'When the service request was supplied to the service provider or agency',
  },
  {
    code: '142',
    label: 'Loss',
    description:
      'When the insured experienced the theft, damage, or casualty to a given property',
  },
  {
    code: '143',
    label: 'Due Date of First Payment to Principal and Interest',
    description:
      'The date first mortgage payment on the principal and interest on a mortgage loan is due',
  },
  {code: '144', label: 'Estimated Acceptance'},
  {code: '145', label: 'Opening Date'},
  {code: '146', label: 'Closing Date'},
  {
    code: '147',
    label: 'Due Date Last Complete Installment Paid',
    description:
      'The date last complete principal and interest mortgage payment made was due',
  },
  {
    code: '148',
    label:
      'Date of Local Office Approval of Conveyance of Damaged Real Estate Property',
    description:
      'The date local office approves of the transfer of damaged property',
  },
  {
    code: '149',
    label: 'Date Deed Filed for Record',
    description:
      'The date the formal document (deed) used to transfer title to real estate is filed for record with the recording authority',
  },
  {code: '150', label: 'Service Period Start'},
  {code: '151', label: 'Service Period End'},
  {
    code: '152',
    label: 'Effective Date of Change',
    description: 'Date on which the change went into effect',
  },
  {code: '153', label: 'Service Interruption'},
  {code: '154', label: 'Adjustment Period Start'},
  {code: '155', label: 'Adjustment Period End'},
  {code: '156', label: 'Allotment Period Start'},
  {code: '157', label: 'Test Period Start'},
  {code: '158', label: 'Test Period Ending'},
  {code: '159', label: 'Bid Price Exception'},
  {code: '160', label: 'Samples to be Returned By'},
  {code: '161', label: 'Loaded on Vessel'},
  {code: '162', label: 'Pending Archive'},
  {code: '163', label: 'Actual Archive'},
  {code: '164', label: 'First Issue'},
  {code: '165', label: 'Final Issue'},
  {code: '166', label: 'Message'},
  {code: '167', label: 'Most Recent Revision (or Initial Version)'},
  {code: '168', label: 'Release'},
  {code: '169', label: 'Product Availability Date'},
  {code: '170', label: 'Supplemental Issue'},
  {code: '171', label: 'Revision'},
  {code: '172', label: 'Correction'},
  {code: '173', label: 'Week Ending'},
  {code: '174', label: 'Month Ending'},
  {code: '175', label: 'Cancel if not shipped by'},
  {code: '176', label: 'Expedited on'},
  {
    code: '177',
    label: 'Cancellation',
    description: 'Date on which the coverage or service is no longer in force',
  },
  {code: '178', label: 'Hold (as of)'},
  {code: '179', label: 'Hold as Stock (as of)'},
  {code: '180', label: 'No Promise (as of)'},
  {code: '181', label: 'Stop Work (as of)'},
  {code: '182', label: 'Will Advise (as of)'},
  {code: '183', label: 'Connection'},
  {code: '184', label: 'Inventory'},
  {code: '185', label: 'Vessel Registry'},
  {
    code: '186',
    label: 'Invoice Period Start',
    description: 'When the billing period covered by an invoice begins',
  },
  {
    code: '187',
    label: 'Invoice Period End',
    description: 'When the billing period covered by an invoice ends',
  },
  {code: '188', label: 'Credit Advice'},
  {code: '189', label: 'Debit Advice'},
  {code: '190', label: 'Released to Vessel'},
  {code: '191', label: 'Material Specification'},
  {code: '192', label: 'Delivery Ticket'},
  {code: '193', label: 'Period Start'},
  {code: '194', label: 'Period End'},
  {code: '195', label: 'Contract Re-Open'},
  {code: '196', label: 'Start'},
  {code: '197', label: 'End'},
  {
    code: '198',
    label: 'Completion',
    description: 'Date when the activity was completed',
  },
  {code: '199', label: 'Seal'},
  {code: '200', label: 'Assembly Start'},
  {code: '201', label: 'Acceptance'},
  {code: '202', label: 'Master Lease Agreement'},
  {
    code: '203',
    label: 'First Produced',
    description:
      'The date of first production of hydrocarbon from a well for the purpose of sales or on-lease use',
  },
  {
    code: '204',
    label: 'Official Rail Car Interchange (Either Actual or Agreed Upon)',
  },
  {code: '205', label: 'Transmitted'},
  {
    code: '206',
    label: 'Status (Outside Processor)',
    description:
      'When outside processor established the status of the material',
  },
  {
    code: '207',
    label: 'Status (Commercial)',
    description: 'When producer established the status of the material',
  },
  {code: '208', label: 'Lot Number Expiration'},
  {code: '209', label: 'Contract Performance Start'},
  {code: '210', label: 'Contract Performance Delivery'},
  {
    code: '211',
    label: 'Service Requested',
    description: 'When warranty repair service was requested',
  },
  {code: '212', label: 'Returned to Customer'},
  {code: '213', label: 'Adjustment to Bill Dated'},
  {code: '214', label: 'Date of Repair/Service'},
  {
    code: '215',
    label: 'Interruption Start',
    description: 'When an interruption in the airplane fueling process began',
  },
  {code: '216', label: 'Interruption End'},
  {code: '217', label: 'Spud', description: 'When drilling started for a well'},
  {
    code: '218',
    label: 'Initial Completion',
    description: 'When well was originally completed',
  },
  {
    code: '219',
    label: 'Plugged and Abandoned',
    description:
      'When a well was plugged and abandoned after discontinuing to produce',
  },
  {code: '220', label: 'Penalty', description: 'Range of penalty period'},
  {
    code: '221',
    label: 'Penalty Begin',
    description: 'Date on which penalty period begins',
  },
  {code: '222', label: 'Birth'},
  {code: '223', label: 'Birth Certificate'},
  {code: '224', label: 'Adoption'},
  {code: '225', label: 'Christening'},
  {code: '226', label: 'Lease Commencement'},
  {code: '227', label: 'Lease Term Start'},
  {code: '228', label: 'Lease Term End'},
  {code: '229', label: 'Rent Start'},
  {code: '230', label: 'Installation'},
  {code: '231', label: 'Progress Payment'},
  {code: '232', label: 'Claim Statement Period Start'},
  {code: '233', label: 'Claim Statement Period End'},
  {code: '234', label: 'Settlement Date'},
  {code: '235', label: 'Delayed Billing (Not Delayed Payment)'},
  {code: '236', label: 'Lender Credit Check'},
  {code: '237', label: 'Student Signed'},
  {
    code: '238',
    label: 'Schedule Release',
    description: 'Planned release date for an item or product',
  },
  {
    code: '239',
    label: 'Baseline',
    description:
      'The baseline or original plan that progress is measured against',
  },
  {
    code: '240',
    label: 'Baseline Start',
    description: 'The start date of baseline or original plan',
  },
  {
    code: '241',
    label: 'Baseline Complete',
    description: 'The complete date of the baseline or original plan',
  },
  {
    code: '242',
    label: 'Actual Start',
    description: 'The actual start date for a task or activity',
  },
  {
    code: '243',
    label: 'Actual Complete',
    description: 'The actual complete date for a task or activity',
  },
  {
    code: '244',
    label: 'Estimated Start',
    description:
      'The revised plan or estimate of the start date for a task or activity',
  },
  {
    code: '245',
    label: 'Estimated Completion',
    description:
      'The revised plan or estimate of the complete date for a task or activity',
  },
  {
    code: '246',
    label: 'Start no earlier than',
    description:
      'Fixed start date for a task or activity – cannot begin before this date',
  },
  {
    code: '247',
    label: 'Start no later than',
    description:
      'Fixed start date for a task or activity – cannot begin after this date',
  },
  {
    code: '248',
    label: 'Finish no later than',
    description:
      'Fixed finish date for a task or activity – cannot end after this date',
  },
  {
    code: '249',
    label: 'Finish no earlier than',
    description:
      'Fixed finish date for a task or activity – cannot end before this date',
  },
  {
    code: '250',
    label: 'Mandatory (or Target) Start',
    description: 'Fixed start date – must begin on this date',
  },
  {
    code: '251',
    label: 'Mandatory (or Target) Finish',
    description: 'Fixed finish date – must finish on this date',
  },
  {
    code: '252',
    label: 'Early Start',
    description: 'The earliest date a task or activity can begin',
  },
  {
    code: '253',
    label: 'Early Finish',
    description: 'The earliest date a task or activity can end',
  },
  {
    code: '254',
    label: 'Late Start',
    description: 'The latest date a task or activity can begin',
  },
  {
    code: '255',
    label: 'Late Finish',
    description: 'The latest date a task or activity can end',
  },
  {
    code: '256',
    label: 'Scheduled Start',
    description:
      'The scheduled start for a task or activity based on resource needs',
  },
  {
    code: '257',
    label: 'Scheduled Finish',
    description:
      'The scheduled finish for a task or activity based on resource needs',
  },
  {
    code: '258',
    label: 'Original Early Start',
    description:
      'The saved baseline or planned early start before current changes',
  },
  {
    code: '259',
    label: 'Original Early Finish',
    description:
      'The saved baseline or planned early finish before current changes',
  },
  {
    code: '260',
    label: 'Rest Day',
    description: 'A non-working day such a Saturday or Sunday',
  },
  {
    code: '261',
    label: 'Rest Start',
    description: 'The beginning of a range for rest days (from)',
  },
  {
    code: '262',
    label: 'Rest Finish',
    description: 'The end of a range of rest days (to)',
  },
  {
    code: '263',
    label: 'Holiday',
    description: 'A non-working day reserved for a company or national holiday',
  },
  {
    code: '264',
    label: 'Holiday Start',
    description: 'The beginning of a range for holidays (from)',
  },
  {
    code: '265',
    label: 'Holiday Finish',
    description: 'The end of a range of holidays (to)',
  },
  {
    code: '266',
    label: 'Base',
    description:
      'The start of base date for a calendar reference – all dates fall after it',
  },
  {
    code: '267',
    label: 'Timenow',
    description: 'The current reporting period reference, or current status',
  },
  {code: '268', label: 'End Date of Support'},
  {code: '269', label: 'Date Account Matures'},
  {code: '270', label: 'Date Filed'},
  {
    code: '271',
    label: 'Penalty End',
    description: 'Date on which penalty period ends',
  },
  {
    code: '272',
    label: 'Exit Plant Date',
    description: 'Date at which the product leaves the manufacturing site',
  },
  {
    code: '273',
    label: 'Latest On Board Carrier Date',
    description: 'Latest date on which the cargo must be on board the carrier',
  },
  {code: '274', label: 'Requested Departure Date'},
  {
    code: '275',
    label: 'Approved',
    description: 'Date report was approved by contractor representative',
  },
  {
    code: '276',
    label: 'Contract Start',
    description: 'The start date for a contract',
  },
  {
    code: '277',
    label: 'Contract Definition',
    description: 'The date the contract was definitized',
  },
  {
    code: '278',
    label: 'Last Item Delivery',
    description: 'The date the last item will be delivered to the customer',
  },
  {
    code: '279',
    label: 'Contract Completion',
    description: 'The finish date for a contract',
  },
  {
    code: '280',
    label:
      'Date Course of Orthodontics Treatment Began or is Expected to Begin',
  },
  {
    code: '281',
    label: 'Over Target Baseline Month',
    description:
      'The date a budget baseline went over the target budget amount',
  },
  {
    code: '282',
    label: 'Previous Report',
    description: 'The date a previous report was submitted',
  },
  {
    code: '283',
    label: 'Funds Appropriation – Start',
    description: 'The date that funds appropriation begins',
  },
  {
    code: '284',
    label: 'Funds Appropriation – End',
    description: 'The date that funds appropriation ends',
  },
  {
    code: '285',
    label: 'Employment or Hire',
    description: 'Date on which the subscriber or dependent is hired',
  },
  {
    code: '286',
    label: 'Retirement',
    description: 'Date on which the subscriber became retired',
  },
  {
    code: '287',
    label: 'Medicare',
    description:
      'Range of dates when the subscriber or dependent is eligible to receive Medicare benefits',
  },
  {
    code: '288',
    label: 'Consolidated Omnibus Budget Reconciliation Act (COBRA)',
    description: 'Range of COBRA coverage',
  },
  {
    code: '289',
    label: 'Premium Paid to Date',
    description: 'Range of dates for which premium has been paid',
  },
  {
    code: '290',
    label: 'Coordination of Benefits',
    description:
      'Range for which coordination of benefits between insurers applies',
  },
  {
    code: '291',
    label: 'Plan',
    description: 'Range of dates for which the plan is in effect',
  },
  {code: '292', label: 'Benefit', description: 'Range of benefit coverage'},
  {
    code: '293',
    label: 'Education',
    description: 'Range of dates when the subscriber or dependent is a student',
  },
  {
    code: '294',
    label: 'Earnings Effective Date',
    description:
      'Date on which earnings become effective for applicable benefits',
  },
  {
    code: '295',
    label: 'Primary Care Provider',
    description:
      'Range of dates for which the services of the primary care provider are in effect',
  },
  {code: '296', label: 'Initial Disability Period Return To Work'},
  {code: '297', label: 'Initial Disability Period Last Day Worked'},
  {
    code: '298',
    label: 'Latest Absence',
    description:
      'Latest occurrence of which the subscriber did not report to work',
  },
  {code: '299', label: 'Illness'},
  {
    code: '300',
    label: 'Enrollment Signature Date',
    description: 'Date subscriber or dependent signed policy enrollment card',
  },
  {
    code: '301',
    label:
      'Consolidated Omnibus Budget Reconciliation Act (COBRA) Qualifying Event',
    description: 'Date of the qualifying event which initiated COBRA benefits',
  },
  {
    code: '302',
    label: 'Maintenance',
    description: 'Date on which the maintenance was performed',
  },
  {
    code: '303',
    label: 'Maintenance Effective',
    description: 'Date on which the maintenance is effective',
  },
  {
    code: '304',
    label: 'Latest Visit or Consultation',
    description:
      'Date subscriber or dependent last visited or consulted with a physician',
  },
  {
    code: '305',
    label: 'Net Credit Service Date',
    description:
      'Date assigned by the employer to an employee who previously worked for them',
  },
  {
    code: '306',
    label: 'Adjustment Effective Date',
    description: 'Date on which the adjustment went into effect',
  },
  {
    code: '307',
    label: 'Eligibility',
    description:
      'Range of dates when the subscriber or dependent were eligible for benefits',
  },
  {code: '308', label: 'Pre-Award Survey'},
  {
    code: '309',
    label: 'Plan Termination',
    description: 'Date on which the plan terminated',
  },
  {
    code: '310',
    label: 'Date of Closing',
    description: 'The date a property is sold',
  },
  {code: '311', label: 'Latest Receiving Date/Cutoff Date'},
  {
    code: '312',
    label: 'Salary Deferral',
    description:
      'Date on which the subscriber begins to defer a portion of their salary',
  },
  {
    code: '313',
    label: 'Cycle',
    description: 'Range of dates as they pertain to a period of time',
  },
  {
    code: '314',
    label: 'Disability',
    description:
      'Range of dates on which the physical or mental condition rendered the subscriber or dependent disabled',
  },
  {
    code: '315',
    label: 'Offset',
    description:
      'Range of dates for which the subscriber or dependent will be receiving other income',
  },
  {
    code: '316',
    label: 'Prior Incorrect Date of Birth',
    description: 'The incorrect date of birth that was previously transmitted',
  },
  {
    code: '317',
    label: 'Corrected Date of Birth',
    description: 'Replaces previously transmitted incorrect date of birth',
  },
  {code: '318', label: 'Added'},
  {code: '319', label: 'Failed'},
  {
    code: '320',
    label: 'Date Foreclosure Proceedings Instituted',
    description:
      'The date the first legal action is taken to terminate an owner’s interest in realty begins',
  },
  {
    code: '321',
    label: 'Purchased',
    description: 'Date product/extended coverage was purchased',
  },
  {
    code: '322',
    label: 'Put into Service',
    description:
      'Date product, subassembly, or replacement part was put in-service',
  },
  {code: '323', label: 'Replaced'},
  {code: '324', label: 'Returned'},
  {code: '325', label: 'Disbursement Date'},
  {code: '326', label: 'Guarantee Date'},
  {code: '327', label: 'Quarter Ending'},
  {code: '328', label: 'Changed'},
  {
    code: '329',
    label: 'Terminated',
    description:
      'Any condition previously reported by the carrier as preventing the completion of a move or service has been terminated',
  },
  {
    code: '330',
    label: 'Referral Date',
    description:
      'The date when an educational official or teacher recommends that a student be evaluated for placement in a special education or other program',
  },
  {
    code: '331',
    label: 'Evaluation Date',
    description:
      'The date on which a student was assessed for placement in a special education or other program',
  },
  {
    code: '332',
    label: 'Placement Date',
    description:
      'The date which identifies when a student was enrolled in a special education or other program',
  },
  {
    code: '333',
    label: 'Individual Education Plan (IEP)',
    description:
      'The date on which the plan specifying the special education instruction and services which a student is to receive was approved',
  },
  {
    code: '334',
    label: 'Re-evaluation Date',
    description:
      'The date on which the student’s placement in special education or other programs will be re-assessed',
  },
  {
    code: '335',
    label: 'Dismissal Date',
    description:
      'The date on which the student exited from a special education or other program',
  },
  {
    code: '336',
    label: 'Employment Begin',
    description: 'Date on which the subscriber or dependent became employed',
  },
  {
    code: '337',
    label: 'Employment End',
    description:
      'Date on which the subscriber or dependent ceased to be employed',
  },
  {
    code: '338',
    label: 'Medicare Begin',
    description: 'Date on which Medicare benefits went into effect',
  },
  {
    code: '339',
    label: 'Medicare End',
    description: 'Date on which Medicare benefits ceased to be in effect',
  },
  {
    code: '340',
    label: 'Consolidated Omnibus Budget Reconciliation Act (COBRA) Begin',
    description: 'Date on which COBRA benefits begin',
  },
  {
    code: '341',
    label: 'Consolidated Omnibus Budget Reconciliation Act (COBRA) End',
    description: 'Date on which COBRA benefits end',
  },
  {
    code: '342',
    label: 'Premium Paid to Date Begin',
    description: 'Date for which premium began to be applied',
  },
  {
    code: '343',
    label: 'Premium Paid to Date End',
    description: 'Date for which premium is paid through',
  },
  {
    code: '344',
    label: 'Coordination of Benefits Begin',
    description: 'Date on which Coordination of Benefits begin',
  },
  {
    code: '345',
    label: 'Coordination of Benefits End',
    description: 'Date on which Coordination of Benefits end',
  },
  {
    code: '346',
    label: 'Plan Begin',
    description: 'Date on which the plan begins',
  },
  {code: '347', label: 'Plan End', description: 'Date on which plan ends'},
  {
    code: '348',
    label: 'Benefit Begin',
    description: 'Date on which the subscriber’s or dependent’s benefit begin',
  },
  {
    code: '349',
    label: 'Benefit End',
    description: 'Date on which the subscriber’s or dependent’s benefit ends',
  },
  {
    code: '350',
    label: 'Education Begin',
    description: 'Date on which the subscriber or dependent became a student',
  },
  {
    code: '351',
    label: 'Education End',
    description:
      'Date on which the subscriber or dependent ceased to be a student',
  },
  {
    code: '352',
    label: 'Primary Care Provider Begin',
    description:
      'Date on which the subscriber’s or dependent’s primary care provider became effective',
  },
  {
    code: '353',
    label: 'Primary Care Provider End',
    description: 'Date on which the primary care provider’s service ended',
  },
  {
    code: '354',
    label: 'Illness Begin',
    description: 'Date on which illness began',
  },
  {
    code: '355',
    label: 'Illness End',
    description: 'Date on which illness ended',
  },
  {
    code: '356',
    label: 'Eligibility Begin',
    description: 'Date on which eligibility begins',
  },
  {
    code: '357',
    label: 'Eligibility End',
    description: 'Date on which eligibility ends',
  },
  {
    code: '358',
    label: 'Cycle Begin',
    description: 'Date on which the cycle begins',
  },
  {
    code: '359',
    label: 'Cycle End',
    description: 'Date on which the cycle ends',
  },
  {
    code: '360',
    label: 'Initial Disability Period Start',
    description: 'Date on which the disability begins',
  },
  {
    code: '361',
    label: 'Initial Disability Period End',
    description: 'Date on which the disability ends',
  },
  {
    code: '362',
    label: 'Offset Begin',
    description: 'Date subscriber or dependent begins receiving other income',
  },
  {
    code: '363',
    label: 'Offset End',
    description: 'Date other income benefits cease',
  },
  {
    code: '364',
    label: 'Plan Period Election Begin',
    description: 'Date on which the pledged contribution begins',
  },
  {
    code: '365',
    label: 'Plan Period Election End',
    description: 'Date on which the pledged contribution ended',
  },
  {
    code: '366',
    label: 'Plan Period Election',
    description:
      'Range of dates for which the subscriber has pledged to contribute toward a benefit',
  },
  {
    code: '367',
    label: 'Due to Customer',
    description: 'Date an item is due to a customer',
  },
  {
    code: '368',
    label: 'Submittal',
    description: 'Date an item was submitted to a customer',
  },
  {code: '369', label: 'Estimated Departure Date'},
  {code: '370', label: 'Actual Departure Date'},
  {code: '371', label: 'Estimated Arrival Date'},
  {code: '372', label: 'Actual Arrival Date'},
  {code: '373', label: 'Order Start'},
  {
    code: '374',
    label: 'Order End',
    description:
      'Point after which data can no longer be used (or becomes invalid for use)',
  },
  {
    code: '375',
    label: 'Delivery Start',
    description: 'The date that deliveries will begin',
  },
  {
    code: '376',
    label: 'Delivery End',
    description: 'The date that deliveries will end',
  },
  {
    code: '377',
    label: 'Contract Costs Through',
    description:
      'Date through which the costs reported in the contractor’s request for progress payment are current',
  },
  {
    code: '378',
    label: 'Financial Information Submission',
    description: 'Date of the submission of contractor financial information',
  },
  {code: '379', label: 'Business Termination'},
  {code: '380', label: 'Applicant Signed'},
  {code: '381', label: 'Cosigner Signed'},
  {code: '382', label: 'Enrollment'},
  {
    code: '383',
    label: 'Adjusted Hire',
    description:
      'Date of rehire is adjusted to give an employee credit for prior years of service, after a break in service has occurred',
  },
  {
    code: '384',
    label: 'Credited Service',
    description:
      'Range of employment service used to determine the amount of plan benefits due the employee',
  },
  {
    code: '385',
    label: 'Credited Service Begin',
    description:
      'The start date from which an employee’s length of service, as defined in the plan document, will be calculated',
  },
  {
    code: '386',
    label: 'Credited Service End',
    description:
      'The end date to be used in the calculation of an employee’s length of service, as defined in the plan document',
  },
  {
    code: '387',
    label: 'Deferred Distribution',
    description:
      'Date to which the participant has elected to defer the distribution of plan benefits (for lump sum payments)',
  },
  {
    code: '388',
    label: 'Payment Commencement',
    description:
      'Date of the initial benefit payment (for installment payments)',
  },
  {
    code: '389',
    label: 'Payroll Period',
    description: 'The applicable payroll date for the financial transaction',
  },
  {
    code: '390',
    label: 'Payroll Period Begin',
    description:
      'The start date of the payroll cycle for the financial transaction',
  },
  {
    code: '391',
    label: 'Payroll Period End',
    description:
      'The end date of the payroll cycle for the financial transaction',
  },
  {
    code: '392',
    label: 'Plan Entry',
    description: 'Date the employee’s participation in the plan is effective',
  },
  {
    code: '393',
    label: 'Plan Participation Suspension',
    description: 'Date the participant is suspended from the plan',
  },
  {
    code: '394',
    label: 'Rehire',
    description: 'Date the participant is rehired, after termination',
  },
  {
    code: '395',
    label: 'Retermination',
    description: 'Date the participant ends employment, after being rehired',
  },
  {
    code: '396',
    label: 'Termination',
    description: 'Date the participant ends employment',
  },
  {
    code: '397',
    label: 'Valuation',
    description:
      'The date when the participant’s total account balance is calculated',
  },
  {
    code: '398',
    label: 'Vesting Service',
    description:
      'Range of employment service used to determine vesting for distribution of plan benefits',
  },
  {
    code: '399',
    label: 'Vesting Service Begin',
    description:
      'The start date from which an employee’s vesting percentage, as defined in the plan document, will be calculated',
  },
  {code: '400', label: 'Vesting Service End'},
  {code: '401', label: 'Duplicate Bill'},
  {code: '402', label: 'Adjustment Promised'},
  {code: '403', label: 'Adjustment Processed'},
  {code: '404', label: 'Year Ending'},
  {
    code: '405',
    label: 'Production',
    description:
      'Used to identify dates and times that operations or processes were performed',
  },
  {
    code: '406',
    label: 'Material Classification',
    description:
      'Used to identify dates and times materials were classified or reclassified',
  },
  {code: '408', label: 'Weighed'},
  {
    code: '409',
    label: 'Date of Deed in Lieu',
    description:
      'The date a voluntary conveyance of a property by deed in lieu is recorded',
  },
  {
    code: '410',
    label: 'Date of Firm Commitment',
    description: 'The date an obligation to ensure the mortgage is issued',
  },
  {
    code: '411',
    label: 'Expiration Date of Extension to Foreclose',
    description: 'The last day of a given time limit to commence foreclosure',
  },
  {
    code: '412',
    label: 'Date of Notice to Convey',
    description:
      'The date of the authorization to assign the contractual rights of a mortgage',
  },
  {
    code: '413',
    label: 'Date of Release of Bankruptcy',
    description: 'The date of release from bankruptcy status',
  },
  {
    code: '414',
    label: 'Optimistic Early Start',
    description: 'Best case earliest date a task or activity can begin',
  },
  {
    code: '415',
    label: 'Optimistic Early Finish',
    description: 'Best case earliest date a task or activity can end',
  },
  {
    code: '416',
    label: 'Optimistic Late Start',
    description: 'Best case latest date a task or activity can begin',
  },
  {
    code: '417',
    label: 'Optimistic Late Finish',
    description: 'Best case latest date a task or activity can end',
  },
  {
    code: '418',
    label: 'Most Likely Early Start',
    description: 'Most likely earliest date a task or activity can begin',
  },
  {
    code: '419',
    label: 'Most Likely Early Finish',
    description: 'Most likely earliest date a task or activity can end',
  },
  {
    code: '420',
    label: 'Most Likely Late Start',
    description: 'Most likely latest date a task or activity can begin',
  },
  {
    code: '421',
    label: 'Most Likely Late Finish',
    description: 'Most likely latest date a task or activity can end',
  },
  {
    code: '422',
    label: 'Pessimistic Early Start',
    description: 'Worst case earliest date a task or activity can begin',
  },
  {
    code: '423',
    label: 'Pessimistic Early Finish',
    description: 'Worst case earliest date a task or activity can end',
  },
  {
    code: '424',
    label: 'Pessimistic Late Start',
    description: 'Worst case latest date a task or activity can begin',
  },
  {
    code: '425',
    label: 'Pessimistic Late Finish',
    description: 'Worst case latest date a task or activity can end',
  },
  {code: '426', label: 'First Payment Due'},
  {code: '427', label: 'First Interest Payment Due'},
  {code: '428', label: 'Subsequent Interest Payment Due'},
  {code: '429', label: 'Irregular Interest Payment Due'},
  {code: '430', label: 'Guarantor Received'},
  {
    code: '431',
    label: 'Onset of Current Symptoms or Illness',
    description: 'Date first symptoms appeared',
  },
  {
    code: '432',
    label: 'Submission',
    description: 'Date on which claim billing information was sent to payer',
  },
  {code: '433', label: 'Removed'},
  {
    code: '434',
    label: 'Statement',
    description: 'Date on which billing document was created',
  },
  {
    code: '435',
    label: 'Admission',
    description: 'Date of entrance to a health care establishment',
  },
  {code: '436', label: 'Insurance Card'},
  {
    code: '437',
    label: 'Spouse Retirement',
    description:
      'Date on which patient’s husband or wife has retired from his or her place of employment',
  },
  {
    code: '438',
    label: 'Onset of Similar Symptoms or Illness',
    description: 'Date symptoms related to current illness first appeared',
  },
  {code: '439', label: 'Accident', description: 'Date mishap occurred'},
  {
    code: '440',
    label: 'Release of Information',
    description:
      'Date patient signified it was acceptable to distribute pertinent information to other parties',
  },
  {
    code: '441',
    label: 'Prior Placement',
    description: 'Date medical device was last placed or arranged',
  },
  {code: '442', label: 'Date of Death', description: 'Date life terminated'},
  {
    code: '443',
    label: 'Peer Review Organization (PRO) Approved Stay',
    description: 'Start and end date of approved confinement',
  },
  {
    code: '444',
    label: 'First Visit or Consultation',
    description: 'Date patient first sought medical assistance',
  },
  {
    code: '445',
    label: 'Initial Placement',
    description: 'Date medical device was positioned or arranged',
  },
  {
    code: '446',
    label: 'Replacement',
    description: 'Date medical device was reinstalled or rearranged',
  },
  {
    code: '447',
    label: 'Occurrence',
    description: 'Date on which an event happened',
  },
  {
    code: '448',
    label: 'Occurrence Span',
    description: 'Start and end date on which a span of events happened',
  },
  {
    code: '449',
    label: 'Occurrence Span From',
    description: 'Date on which span of events started',
  },
  {
    code: '450',
    label: 'Occurrence Span To',
    description: 'Date on which span of events ended',
  },
  {
    code: '451',
    label: 'Initial Fee Due',
    description: 'Date the first of one or more payments is due',
  },
  {
    code: '452',
    label: 'Appliance Placement',
    description: 'Date orthodontic devices were arranged',
  },
  {
    code: '453',
    label: 'Acute Manifestation of a Chronic Condition',
    description: 'Date serious symptoms were exhibited for a long term illness',
  },
  {
    code: '454',
    label: 'Initial Treatment',
    description: 'Date medical treatment first began',
  },
  {
    code: '455',
    label: 'Last X-Ray',
    description: 'Date of the most recent x-ray',
  },
  {
    code: '456',
    label: 'Surgery',
    description: 'Date on which operation was performed',
  },
  {
    code: '457',
    label: 'Continuous Passive Motion (CPM)',
    description:
      'Date patient began using a device to provide uninterrupted motion not produced by active efforts',
  },
  {
    code: '458',
    label: 'Certification',
    description: 'Date of a document attesting to a fact',
  },
  {
    code: '459',
    label: 'Nursing Home From',
    description: 'Date on which patient entered nursing home',
  },
  {
    code: '460',
    label: 'Nursing Home To',
    description: 'Date on which patient was released from nursing home',
  },
  {
    code: '461',
    label: 'Last Certification',
    description: 'Date of the most recent document attesting to a fact',
  },
  {
    code: '462',
    label:
      'Date of Local Office Approval of Conveyance of Occupied Real Estate Property',
    description:
      'The date local office approves of the transfer of an occupied property',
  },
  {
    code: '463',
    label: 'Begin Therapy',
    description: 'Date treatment of physical or mental disorder started',
  },
  {
    code: '464',
    label: 'Oxygen Therapy From',
    description: 'Date treatment with oxygen started',
  },
  {
    code: '465',
    label: 'Oxygen Therapy To',
    description: 'Date treatment with oxygen ceased',
  },
  {
    code: '466',
    label: 'Oxygen Therapy',
    description: 'Begin and end dates of treatment with oxygen',
  },
  {
    code: '467',
    label: 'Signature',
    description: 'Date on which the accompanying signature was received',
  },
  {
    code: '468',
    label: 'Prescription Fill',
    description: 'Date on which drug order was filled',
  },
  {
    code: '469',
    label: 'Provider Signature',
    description:
      'Date supplier of medical service signified the service in question was, in fact, provided',
  },
  {
    code: '470',
    label:
      'Date of Local Office Certification of Conveyance of Damaged Real Estate Property',
    description:
      'The date local office approves of the transfer of a damaged property or the date of the mortgagee’s certification that adequate fire insurance was not obtainable',
  },
  {
    code: '471',
    label: 'Prescription',
    description: 'Date on which prescription was written',
  },
  {code: '472', label: 'Service'},
  {
    code: '473',
    label: 'Medicaid Begin',
    description: 'Date patient became eligible for Medicaid benefits',
  },
  {
    code: '474',
    label: 'Medicaid End',
    description: 'Date patient no longer eligible for Medicaid benefits',
  },
  {
    code: '475',
    label: 'Medicaid',
    description: 'Start and end of effective period for Medicaid',
  },
  {
    code: '476',
    label: 'Peer Review Organization (PRO) Approved Stay From',
    description: 'Date start of confinement was approved',
  },
  {
    code: '477',
    label: 'Peer Review Organization (PRO) Approved Stay To',
    description: 'Date confinement approval ceased',
  },
  {
    code: '478',
    label: 'Prescription From',
    description: 'Start of prescription period',
  },
  {
    code: '479',
    label: 'Prescription To',
    description: 'End of prescription period',
  },
  {
    code: '480',
    label: 'Arterial Blood Gas Test',
    description:
      'Date of test to determine gas content in blood circulating from the heart, at rest, breathing room air',
  },
  {
    code: '481',
    label: 'Oxygen Saturation Test',
    description: 'Date on which oxygen saturation testing occurred',
  },
  {
    code: '482',
    label: 'Pregnancy Begin',
    description: 'Date pregnancy estimated to begin',
  },
  {
    code: '483',
    label: 'Pregnancy End',
    description: 'Date pregnancy end, whether normal or abnormal delivery',
  },
  {code: '484', label: 'Last Menstrual Period'},
  {
    code: '485',
    label: 'Injury Begin',
    description: 'Date damage was inflicted to the body by an external source',
  },
  {
    code: '486',
    label: 'Injury End',
    description:
      'Date damage inflicted to the body by an external source ceased',
  },
  {
    code: '487',
    label: 'Nursing Home',
    description: 'Facility for those unable to care for themselves',
  },
  {
    code: '488',
    label: 'Collateral Dependent',
    description: 'Range of collateral dependent coverage',
  },
  {
    code: '489',
    label: 'Collateral Dependent Begin',
    description: 'Date on which collateral dependent coverage begins',
  },
  {
    code: '490',
    label: 'Collateral Dependent End',
    description: 'Date on which collateral dependent coverage ends',
  },
  {
    code: '491',
    label: 'Sponsored Dependent',
    description: 'Range of sponsoring dependent coverage',
  },
  {
    code: '492',
    label: 'Sponsored Dependent Begin',
    description: 'Date on which sponsored dependent coverage begins',
  },
  {
    code: '493',
    label: 'Sponsored Dependent End',
    description: 'Date on which sponsored dependent coverage ends',
  },
  {
    code: '494',
    label: 'Deductible',
    description: 'Date for which deductible is applied',
  },
  {
    code: '495',
    label: 'Out-of-Pocket',
    description: 'Date for which out of pocket is applied',
  },
  {code: '496', label: 'Contract Audit Date'},
  {code: '497', label: 'Latest Delivery Date at Pier'},
  {
    code: '498',
    label: 'Mortgagee Reported Curtailment Date',
    description:
      'The date the mortgagee failed to meet a mandatory time requirement',
  },
  {
    code: '499',
    label: 'Mortgagee Official Signature Date',
    description:
      'The date the mortgagee’s representative signs the claim for mortgage insurance benefits form',
  },
  {
    code: '500',
    label: 'Resubmission',
    description:
      'Date when original transaction should be resubmitted for further consideration',
  },
  {
    code: '501',
    label: 'Expected Reply',
    description: 'Date when response may be expected',
  },
  {
    code: '502',
    label: 'Dropped to Less than Half Time',
    description:
      'Date on which the student ceased to be enrolled at least half time',
  },
  {
    code: '503',
    label: 'Repayment Begin',
    description: 'Date on which repayment of the loan begins',
  },
  {
    code: '504',
    label: 'Loan Servicing Transfer',
    description:
      'Date on which the loan is transferred from one servicer to another servicer',
  },
  {
    code: '505',
    label: 'Loan Purchase',
    description: 'Date on which the ownership of the loan changes',
  },
  {
    code: '506',
    label: 'Last Notification',
    description:
      'Date on which the lender was last notified of the less-than-half time information',
  },
  {
    code: '507',
    label: 'Extract',
    description: 'Date on which the file was extracted from the database',
  },
  {
    code: '508',
    label: 'Extended',
    description: 'The extended date for a performance requirement',
  },
  {
    code: '509',
    label: 'Servicer Signature Date',
    description:
      'The date an authorized representative of the lender servicing the mortgage signs the claim for mortgage insurance benefits form',
  },
  {code: '510', label: 'Date Packed'},
  {code: '511', label: 'Shelf Life Expiration'},
  {code: '512', label: 'Warranty Expiration'},
  {code: '513', label: 'Overhauled'},
  {code: '514', label: 'Transferred'},
  {code: '515', label: 'Notified'},
  {code: '516', label: 'Discovered'},
  {code: '517', label: 'Inspected'},
  {code: '518', label: 'Voucher (Date of)'},
  {
    code: '519',
    label: 'Date Bankruptcy Filed',
    description: 'Date of last bankruptcy action',
  },
  {
    code: '520',
    label: 'Date of Damage',
    description: 'The date that the property became damaged',
  },
  {
    code: '521',
    label: 'Date Hazard Insurance Policy Cancelled',
    description: 'The date the hazard insurance policy was cancelled',
  },
  {
    code: '522',
    label: 'Expiration Date to Submit Title Evidence',
    description:
      'The last day of an approved extension to submit title evidence without penalty',
  },
  {
    code: '523',
    label: 'Date of Claim',
    description: 'The date the claim form is prepared',
  },
  {
    code: '524',
    label: 'Date of Notice of Referral for Assignment',
    description:
      'The date mortgagors are notified in writing that it is their lender’s opinion that they are qualified for an assignment program and that their defaulted mortgage should be assigned',
  },
  {
    code: '525',
    label: 'Date of Notice of Probable Ineligibility for Assignment',
    description:
      'The date mortgagors are notified in writing that it is their lender’s opinion that they are not qualified for an assignment program but they may apply directly to the program sponsor for consideration',
  },
  {
    code: '526',
    label: 'Date of Foreclosure Notice',
    description:
      'The date the mortgagee notifies the mortgage insurer that foreclosure has been initiated',
  },
  {
    code: '527',
    label: 'Expiration of Foreclosure Timeframe',
    description:
      'The last day of an extension of the time requirement to institute foreclosure proceedings on an insured mortgage',
  },
  {
    code: '528',
    label: 'Date Possessory Action Initiated',
    description:
      'The date the mortgagee initiates action to take possession of a property secured by a mortgage in default',
  },
  {
    code: '529',
    label: 'Date of Possession',
    description:
      'The date a mortgagee acquires possession of a property secured by a mortgage in default',
  },
  {code: '530', label: 'Date of Last Installment Received'},
  {
    code: '531',
    label: 'Date of Acquisition of Title',
    description:
      'The date the mortgagee acquires good and marketable title to a property secured by a mortgage in default',
  },
  {
    code: '532',
    label: 'Expiration of Extension to Convey',
    description:
      'The last day of an extension of the time requirement to convey a property',
  },
  {
    code: '533',
    label: 'Date of Assignment Approval',
    description:
      'The date approval is given to assign the contract rights of a mortgage',
  },
  {
    code: '534',
    label: 'Date of Assignment Rejection',
    description:
      'The date a request to assign the contract rights of a mortgage is rejected',
  },
  {
    code: '535',
    label: 'Curtailment Date from Advice of Payment',
    description: 'The date to which interest was curtailed',
  },
  {
    code: '536',
    label: 'Expiration of Extension to Submit Fiscal Data',
    description:
      'The date of the expiration of extension given to submit fiscal data',
  },
  {code: '537', label: 'Date Documentation, or Paperwork, or Both Was Sent'},
  {code: '538', label: 'Makegood Commercial Date'},
  {code: '539', label: 'Policy Effective'},
  {code: '540', label: 'Policy Expiration'},
  {code: '541', label: 'Employee Effective Date of Coverage'},
  {
    code: '542',
    label: 'Claim Administrator Notified of Employee Legal Representation',
  },
  {code: '543', label: 'Last Premium Paid Date'},
  {code: '544', label: 'Employer Knowledge of the Injury'},
  {code: '545', label: 'Claim Administrator Knowledge of the Injury'},
  {code: '546', label: 'Date of Maximum Medical Improvement'},
  {code: '547', label: 'Date of Loan'},
  {code: '548', label: 'Date of Advance'},
  {
    code: '549',
    label: 'Beginning Lay Date',
    description: 'First date a vessel may call at the port',
  },
  {code: '550', label: 'Certificate Effective'},
  {code: '551', label: 'Benefit Application Date'},
  {code: '552', label: 'Actual Return to Work'},
  {code: '553', label: 'Released Return to Work'},
  {
    code: '554',
    label: 'Ending Lay Date',
    description: 'Date by which the vessel will call at the port',
  },
  {code: '555', label: 'Employee Wages Ceased'},
  {code: '556', label: 'Last Salary Increase'},
  {code: '557', label: 'Employee Laid Off'},
  {code: '558', label: 'Injury or Illness'},
  {
    code: '559',
    label: 'Oldest Unpaid Installment',
    description:
      'The due date of the oldest complete mortgage payment that has not been paid',
  },
  {
    code: '560',
    label: 'Preforeclosure Acceptance Date',
    description:
      'The date that the mortgagor and the mortgagee accepts a preforeclosure acceptance plan',
  },
  {
    code: '561',
    label: 'Preforeclosure Sale Closing Date',
    description:
      'The date that the mortgagor and the mortgagee accept a preforeclosure sales closing plan',
  },
  {code: '562', label: 'Date of First Uncured Default'},
  {code: '563', label: 'Date Default Was Cured'},
  {code: '564', label: 'Date of First Mortgage Payment'},
  {code: '565', label: 'Date of Property Inspection'},
  {code: '566', label: 'Date Total Amount of Delinquency Reported'},
  {code: '567', label: 'Date Outstanding Loan Balance Reported'},
  {code: '568', label: 'Date Foreclosure Sale Scheduled'},
  {code: '569', label: 'Date Foreclosure Held'},
  {code: '570', label: 'Date Redemption Period Ends'},
  {
    code: '571',
    label: 'Date Voluntary Conveyance Accepted',
    description:
      'The date voluntary conveyance of the property by the mortgagee holding the defaulted mortgage is accepted by the Federal Housing Commissioner',
  },
  {code: '572', label: 'Date Property Sold'},
  {code: '573', label: 'Date Claim Paid'},
  {
    code: '574',
    label: 'Action Begin Date',
    description:
      'The date a resolution action such as scheduling a repayment plan or a delay action such as Chapter 7 bankruptcy filing begins',
  },
  {code: '575', label: 'Projected Action End Date'},
  {code: '576', label: 'Action End Date'},
  {code: '577', label: 'Original Maturity Date'},
  {code: '578', label: 'Date Referred to Attorney for Foreclosure'},
  {code: '579', label: 'Planned Release'},
  {code: '580', label: 'Actual Release'},
  {code: '581', label: 'Contract Period'},
  {code: '582', label: 'Report Period'},
  {code: '583', label: 'Suspension'},
  {code: '584', label: 'Reinstatement'},
  {code: '585', label: 'Report'},
  {code: '586', label: 'First Contact'},
  {code: '587', label: 'Projected Foreclosure Sale Date'},
  {
    code: '589',
    label: 'Date Assignment Filed for Record',
    description:
      'The date the transfer of the contract right to real estate is filed for record with the recording authority',
  },
  {
    code: '590',
    label: 'Date of Appraisal',
    description: 'The date the value of a property is assessed',
  },
  {
    code: '591',
    label: 'Expiration Date of Extension to Assign',
    description:
      'The last day of a given time limit to assign the contractual rights of a mortgage',
  },
  {
    code: '592',
    label: 'Date of Extension to Convey',
    description:
      'The last day of a given time limit to complete the transfer of real property',
  },
  {
    code: '593',
    label: 'Date Hazard Insurance Policy Refused',
    description:
      'The date the insurance carrier refused to renew the policy on a property',
  },
  {
    code: '594',
    label: 'High Fabrication Release Authorization',
    description:
      'The date of the purchaser’s program number that authorizes the highest amount of material production',
  },
  {
    code: '595',
    label: 'High Raw Material Authorization',
    description:
      'The date of the purchaser’s program number that authorizes the highest amount of raw material or purchased components in their original state to be secured for production',
  },
  {code: '596', label: 'Material Change Notice'},
  {code: '597', label: 'Latest Delivery Date at Rail Ramp'},
  {code: '598', label: 'Rejected'},
  {code: '599', label: 'Repayment Schedule Sent'},
  {code: '600', label: 'As Of'},
  {code: '601', label: 'First Submission'},
  {code: '602', label: 'Subsequent Submission'},
  {code: '603', label: 'Renewal'},
  {code: '604', label: 'Withdrawn'},
  {code: '606', label: 'Certification Period Start'},
  {code: '607', label: 'Certification Revision'},
  {code: '608', label: 'Continuous Coverage Date(s)'},
  {code: '609', label: 'Prearranged Deal Match'},
  {code: '610', label: 'Contingency End'},
  {
    code: '611',
    label: 'Oxygen Therapy Evaluation',
    description:
      'The date the patient was last examined by the physician to determine the need for oxygen therapy',
  },
  {
    code: '612',
    label: 'Shut In',
    description:
      'The date when the valves are closed or the pump is stopped at the wellhead',
  },
  {
    code: '613',
    label: 'Allowable Effective',
    description: 'The date the report is effective for allowable purposes',
  },
  {code: '614', label: 'First Sales'},
  {code: '615', label: 'Date Acquired'},
  {code: '616', label: 'Interviewer Signed'},
  {code: '617', label: 'Application Logged Date'},
  {code: '618', label: 'Review Date'},
  {code: '619', label: 'Decision Date'},
  {code: '620', label: 'Previously Resided'},
  {code: '621', label: 'Reported'},
  {code: '622', label: 'Checked'},
  {code: '623', label: 'Settled'},
  {code: '624', label: 'Presently Residing'},
  {code: '625', label: 'Employed in this Position'},
  {code: '626', label: 'Verified'},
  {
    code: '627',
    label: 'Second Admission Date',
    description:
      'Date of second admission to a health care facility which occurred during the service period of this claim',
  },
  {code: '629', label: 'Account Opened'},
  {code: '630', label: 'Account Closed'},
  {code: '631', label: 'Property Acquired'},
  {code: '632', label: 'Property Built'},
  {code: '633', label: 'Employed in this Profession'},
  {code: '634', label: 'Next Review Date'},
  {code: '635', label: 'Initial Contact Date'},
  {code: '636', label: 'Date of Last Update'},
  {
    code: '637',
    label: 'Second Discharge Date',
    description:
      'Date of second discharge from a health care facility which occurred during the service period of this claim',
  },
  {code: '638', label: 'Date of Last Draw'},
  {code: '640', label: 'Complaint'},
  {code: '641', label: 'Option'},
  {code: '642', label: 'Solicitation'},
  {code: '643', label: 'Clause'},
  {code: '644', label: 'Meeting'},
  {code: '646', label: 'Rental Period'},
  {code: '647', label: 'Next Pay Increase'},
  {code: '648', label: 'Period Covered by Source Documents'},
  {code: '649', label: 'Document Due'},
  {code: '650', label: 'Court Notice'},
  {code: '651', label: 'Expected Funding Date'},
  {code: '652', label: 'Assignment Recorded'},
  {code: '653', label: 'Case Reopened'},
  {code: '655', label: 'Previous Court Event'},
  {code: '656', label: 'Last Date to Object'},
  {code: '657', label: 'Court Event'},
  {code: '658', label: 'Last Date to File a Claim'},
  {code: '659', label: 'Case Converted'},
  {code: '660', label: 'Debt Incurred'},
  {code: '661', label: 'Judgment'},
  {code: '662', label: 'Wages Start'},
  {code: '663', label: 'Wages End'},
  {code: '664', label: 'Date Through Which Property Taxes Have Been Paid'},
  {code: '665', label: 'Paid Through Date'},
  {code: '666', label: 'Date Paid'},
  {code: '667', label: 'Anesthesia Administration'},
  {code: '668', label: 'Price Protection'},
  {
    code: '669',
    label: 'Claim Incurred',
    description: 'The date a debt or obligation arises or is incurred',
  },
  {code: '670', label: 'Book Entry Delivery'},
  {code: '671', label: 'Rate Adjustment'},
  {code: '672', label: 'Next Installment Due Date'},
  {
    code: '673',
    label: 'Daylight Overdraft Time',
    description: 'Time a Fed Wire is processed by the Federal Reserve',
  },
  {
    code: '674',
    label: 'Presentment Date',
    description: 'Date a Letter of Credit is presented',
  },
  {
    code: '675',
    label: 'Negotiated Extension Date',
    description: 'New date to which a Letter of Credit extension is negotiated',
  },
  {code: '681', label: 'Remittance'},
  {code: '682', label: 'Security Rate Adjustment'},
  {code: '683', label: 'Filing Period'},
  {code: '684', label: 'Review Period End'},
  {code: '685', label: 'Requested Settlement'},
  {code: '686', label: 'Last Screening'},
  {code: '687', label: 'Confinement'},
  {code: '688', label: 'Arrested'},
  {code: '689', label: 'Convicted'},
  {code: '690', label: 'Interviewed'},
  {code: '691', label: 'Last Visit'},
  {code: '692', label: 'Recovery'},
  {code: '693', label: 'Time in U.S.'},
  {
    code: '694',
    label: 'Future Period',
    description: 'Range of future activity',
  },
  {
    code: '695',
    label: 'Previous Period',
    description: 'Range of previous activity',
  },
  {code: '696', label: 'Interest Paid To'},
  {code: '697', label: 'Date of Seizure'},
  {
    code: '699',
    label: 'Setoff',
    description:
      'Date creditor reduced claim by amount of debtor’s claim against creditor',
  },
  {code: '700', label: 'Override Date for Settlement'},
  {
    code: '701',
    label: 'Settlement Date (From Interline Settlement System (ISS) only)',
  },
  {code: '702', label: 'Sending Road Time Stamp'},
  {code: '703', label: 'Retransmission Time Stamp'},
  {
    code: '704',
    label: 'Delivery Appointment Date and Time',
    description: 'The date and time associated with an appointment number',
  },
  {code: '705', label: 'Interest Paid Through'},
  {code: '706', label: 'Date Material Usage Suspended'},
  {code: '707', label: 'Last Payment Made'},
  {code: '708', label: 'Past Due'},
  {code: '709', label: 'Analysis Month Ending'},
  {code: '710', label: 'Date of Specification'},
  {code: '711', label: 'Date of Standard'},
  {code: '712', label: 'Return to Work Part Time'},
  {code: '713', label: 'Paid-through Date for Salary Continuation'},
  {code: '714', label: 'Paid-through Date for Vacation Pay'},
  {code: '715', label: 'Paid-through Date for Accrued Sick Pay'},
  {code: '716', label: 'Appraisal Ordered'},
  {code: '717', label: 'Date of Operation'},
  {code: '718', label: 'Best Time to Call'},
  {code: '719', label: 'Verbal Report Needed'},
  {code: '720', label: 'Estimated Escrow Closing'},
  {code: '721', label: 'Permit Year'},
  {code: '722', label: 'Remodeling Completed'},
  {code: '723', label: 'Current Month Ending'},
  {code: '724', label: 'Previous Month Ending'},
  {code: '725', label: 'Cycle to Date'},
  {code: '726', label: 'Year to Date'},
  {code: '727', label: 'On Hold'},
  {code: '728', label: 'Off Hold'},
  {code: '729', label: 'Facsimile Due By'},
  {code: '730', label: 'Reporting Cycle Date'},
  {code: '731', label: 'Last Paid Installment Date'},
  {
    code: '732',
    label: 'Claims Made',
    description:
      'Date that determines Liability coverage under a Claims Made policy',
  },
  {code: '733', label: 'Date of Last Payment Received'},
  {code: '734', label: 'Curtailment Date'},
  {code: '736', label: 'Pool Settlement'},
  {code: '737', label: 'Next Interest Change Date'},
  {code: '738', label: 'Most Recent Hemoglobin or Hematocrit or Both'},
  {code: '739', label: 'Most Recent Serum Creatine'},
  {code: '740', label: 'Closed'},
  {code: '741', label: 'Therapy'},
  {code: '742', label: 'Implantation'},
  {code: '743', label: 'Explantation'},
  {code: '744', label: 'Date Became Aware'},
  {code: '745', label: 'First Marketed'},
  {code: '746', label: 'Last Marketed'},
  {
    code: '747',
    label: 'New Due Date of First Payment to Principal and Interest',
  },
  {code: '748', label: 'New Maturity Date'},
  {code: '749', label: 'Current'},
  {
    code: '750',
    label: 'Expected Problem Resolution',
    description:
      'The date and time by which resolution of a problem is expected to occur',
  },
  {
    code: '751',
    label: 'Alternate Problem Resolution',
    description:
      'The alternate date and time by which resolution of a problem should occur, if the expected problem resolution date and time cannot be met',
  },
  {code: '752', label: 'Fee Capitalization'},
  {code: '753', label: 'Interest Capitalization'},
  {code: '754', label: 'Next Payment Due'},
  {code: '755', label: 'Conversion to Repayment'},
  {code: '756', label: 'End of Grace Period'},
  {code: '757', label: 'School Refund'},
  {code: '758', label: 'Simple Interest Due'},
  {code: '759', label: 'Date Practice Ceased'},
  {code: '760', label: 'Printed'},
  {code: '761', label: 'Date Practice Established'},
  {code: '762', label: 'Drop Action Date'},
  {code: '764', label: 'Most Recent Renewal'},
  {code: '765', label: 'Original'},
  {code: '766', label: 'Outside Auditor’s Report'},
  {code: '769', label: 'Pre-certification Date'},
  {code: '770', label: 'Back on Market'},
  {code: '771', label: 'Status'},
  {code: '772', label: 'Benefit Adjustment Start'},
  {code: '773', label: 'Off-Market'},
  {code: '774', label: 'Tour'},
  {code: '775', label: 'Benefit Adjustment End'},
  {code: '776', label: 'Listing Received'},
  {code: '777', label: 'Benefit Adjustment Period'},
  {code: '778', label: 'Anticipated Closing'},
  {code: '779', label: 'Last Publication'},
  {
    code: '780',
    label: 'Sold Book Publication',
    description: 'Publication Date of Multiple Listing Book of Sold Property',
  },
  {code: '781', label: 'Occupancy'},
  {code: '782', label: 'Contingency'},
  {code: '783', label: 'Percolation Test'},
  {code: '784', label: 'Septic Approval'},
  {code: '785', label: 'Title Transfer'},
  {code: '786', label: 'Open House'},
  {code: '787', label: 'Benefit Credit Period'},
  {code: '788', label: 'Benefit Transfer Period'},
  {code: '789', label: 'Homestead'},
  {code: '790', label: 'Sanction'},
  {
    code: '791',
    label: 'Tail Coverage Begin',
    description: 'Extension of liability coverage begin date',
  },
  {
    code: '792',
    label: 'Tail Coverage End',
    description: 'Extension of liability coverage end date',
  },
  {code: '793', label: 'Training Begin'},
  {code: '794', label: 'Training End'},
  {code: '795', label: 'Verification Received'},
  {code: '796', label: 'Verification Sent'},
  {code: '797', label: 'State Residency Date'},
  {code: '798', label: 'Effective Date of the Routing File'},
  {code: '799', label: 'Test Data Analysis'},
  {code: '800', label: 'Midpoint of Performance'},
  {code: '801', label: 'Acquisition Date'},
  {code: '802', label: 'Date of Action'},
  {code: '803', label: 'Paid in Full'},
  {code: '804', label: 'Refinance'},
  {code: '805', label: 'Voluntary Termination'},
  {code: '806', label: 'Customer Order'},
  {code: '807', label: 'Stored'},
  {code: '808', label: 'Selected'},
  {code: '809', label: 'Posted'},
  {code: '810', label: 'Document Received'},
  {code: '811', label: 'Rebuilt'},
  {code: '812', label: 'Marriage'},
  {code: '813', label: 'Customs Entry Date'},
  {code: '814', label: 'Payment Due Date'},
  {code: '815', label: 'Maturity Date'},
  {code: '816', label: 'Trade Date'},
  {code: '817', label: 'Gallons Per Minute (GPM) Test Performed'},
  {code: '818', label: 'British Thermal Unit (BTU) Test Performed'},
  {code: '819', label: 'Last Accounts Filed at Public Registration Agency'},
  {code: '820', label: 'Real Estate Tax Year'},
  {code: '821', label: 'Final Reconciliation Value Estimate as of'},
  {code: '822', label: 'Map'},
  {code: '823', label: 'Opinion'},
  {code: '824', label: 'Version'},
  {code: '825', label: 'Original Due Date'},
  {code: '826', label: 'Incumbency Period'},
  {code: '827', label: 'Audience Deficiency Period'},
  {code: '828', label: 'Aired Date'},
  {code: '830', label: 'Schedule'},
  {code: '831', label: 'Paid Through Date for Minimum Payment'},
  {code: '832', label: 'Paid Through Date for Total Payment'},
  {code: '840', label: 'Election'},
  {code: '841', label: 'Engineering Data List'},
  {code: '842', label: 'Last Production'},
  {code: '843', label: 'Not Before'},
  {code: '844', label: 'Not After'},
  {code: '845', label: 'Initial Claim'},
  {code: '846', label: 'Benefits Paid'},
  {code: '847', label: 'Wages Earned'},
  {code: '848', label: 'Adjusted Start'},
  {code: '849', label: 'Adjusted End'},
  {code: '850', label: 'Revised Adjusted Start'},
  {code: '851', label: 'Revised Adjusted End'},
  {code: '853', label: 'Field Test'},
  {code: '854', label: 'Mortgage Note Date'},
  {code: '855', label: 'Alternative Due Date'},
  {code: '856', label: 'First Payment Change'},
  {code: '857', label: 'First Rate Adjustment'},
  {code: '858', label: 'Alternate Base Period'},
  {code: '859', label: 'Prior Notice'},
  {code: '860', label: 'Appointment Effective'},
  {code: '861', label: 'Appointment Expiration'},
  {code: '862', label: 'Company Termination'},
  {code: '863', label: 'Continuing Education Requirement'},
  {code: '864', label: 'Distributor Effective'},
  {code: '865', label: 'Distributor Termination'},
  {code: '866', label: 'Examination'},
  {code: '867', label: 'Incorporation Dissolution'},
  {code: '868', label: 'Last Follow-up'},
  {code: '869', label: 'License Effective'},
  {code: '870', label: 'License Expiration'},
  {code: '871', label: 'License Renewal'},
  {code: '872', label: 'License Requested'},
  {code: '873', label: 'Mailed'},
  {code: '874', label: 'Paperwork Mailed'},
  {code: '875', label: 'Previous Employment'},
  {code: '876', label: 'Previous Employment End'},
  {code: '877', label: 'Previous Employment Start'},
  {code: '878', label: 'Previous Residence'},
  {code: '879', label: 'Previous Residence End'},
  {code: '880', label: 'Previous Residence Start'},
  {code: '881', label: 'Request'},
  {code: '882', label: 'Resident License Effective'},
  {code: '883', label: 'Resident License Expiration'},
  {code: '884', label: 'State Termination'},
  {code: '885', label: 'Texas Line Termination'},
  {code: '900', label: 'Acceleration'},
  {code: '901', label: 'Adjusted Contestability'},
  {code: '903', label: 'Application Entry'},
  {code: '904', label: 'Approval/Offer'},
  {code: '905', label: 'Automatic Premium Loan'},
  {code: '906', label: 'Collection'},
  {code: '907', label: 'Confinement End'},
  {code: '908', label: 'Confinement Start'},
  {code: '909', label: 'Contestability'},
  {code: '910', label: 'Flat Extra End'},
  {code: '911', label: 'Last Activity'},
  {code: '912', label: 'Last Change'},
  {code: '913', label: 'Last Episode'},
  {code: '914', label: 'Last Meal'},
  {code: '915', label: 'Loan'},
  {code: '916', label: 'Application Status'},
  {code: '917', label: 'Maturity'},
  {code: '918', label: 'Medical Information Signature'},
  {code: '919', label: 'Medical Information System'},
  {code: '920', label: 'Note'},
  {code: '921', label: 'Offer Expiration'},
  {code: '922', label: 'Original Receipt'},
  {code: '923', label: 'Placement'},
  {code: '924', label: 'Placement Period Expiration'},
  {code: '925', label: 'Processing'},
  {code: '926', label: 'Recapture'},
  {code: '927', label: 'Re-entry'},
  {code: '928', label: 'Reissue'},
  {code: '930', label: 'Requalification'},
  {code: '931', label: 'Reinsurance Effective'},
  {code: '932', label: 'Reservation of Facility'},
  {code: '933', label: 'Settlement Status'},
  {code: '934', label: 'Table Rating End'},
  {code: '935', label: 'Termination of Facility'},
  {code: '936', label: 'Treatment'},
  {code: '937', label: 'Department of Labor Wage Determination Date'},
  {code: '938', label: 'Order'},
  {code: '939', label: 'Resolved'},
  {code: '940', label: 'Execution Date'},
  {code: '941', label: 'Capitation Period Start'},
  {code: '942', label: 'Capitation Period End'},
  {code: '943', label: 'Last Date for a Government Agency to File a Claim'},
  {code: '944', label: 'Adjustment Period'},
  {code: '945', label: 'Activity'},
  {code: '946', label: 'Mail By'},
  {code: '947', label: 'Preparation'},
  {code: '948', label: 'Payment Initiated'},
  {code: '949', label: 'Payment Effective'},
  {code: '950', label: 'Application'},
  {code: '951', label: 'Reclassification'},
  {code: '952', label: 'Reclassification (Exit Date)'},
  {code: '953', label: 'Post-Reclassification'},
  {code: '954', label: 'Post-Reclassification (First Report Card)'},
  {code: '955', label: 'Post-Reclassification (First Semi-annual)'},
  {code: '956', label: 'Post-Reclassification (Second Semi-annual)'},
  {code: '957', label: 'Post-Reclassification (End of Second Year)'},
  {code: '960', label: 'Adjusted Death Benefit'},
  {code: '961', label: 'Anniversary'},
  {code: '962', label: 'Annuitization'},
  {
    code: '963',
    label: 'Annuity Commencement Date',
    description: 'Expiration of annuity accumulation contract',
  },
  {code: '964', label: 'Bill'},
  {
    code: '965',
    label: 'Calendar Anniversary',
    description: 'Adjusted plan anniversary date',
  },
  {code: '966', label: 'Contract Mailed'},
  {
    code: '967',
    label: 'Early Withdrawal',
    description: 'Pre age 59 withdrawal from qualified plan',
  },
  {code: '968', label: 'Fiscal Anniversary'},
  {code: '969', label: 'Income', description: 'Date annuity payments commence'},
  {code: '970', label: 'Initial Premium'},
  {code: '971', label: 'Initial Premium Effective'},
  {code: '972', label: 'Last Premium Effective'},
  {
    code: '973',
    label: 'Minimum Required Distribution',
    description:
      'Internal Revenue Service required latest distribution start date',
  },
  {code: '974', label: 'Next Anniversary'},
  {code: '975', label: 'Notice'},
  {code: '976', label: 'Notification of Death'},
  {code: '977', label: 'Partial Annuitization'},
  {
    code: '978',
    label: 'Plan Anniversary',
    description: 'Anniversary date adjusted to nearest business day',
  },
  {code: '979', label: 'Policy Surrender'},
  {code: '980', label: 'Prior Contract Anniversary'},
  {code: '981', label: 'Prior Contract Issue'},
  {code: '982', label: 'Signature Received'},
  {code: '983', label: 'Tax'},
  {code: '984', label: 'Benefit Period'},
  {code: '985', label: 'Month to Date'},
  {code: '986', label: 'Semiannual Ending'},
  {code: '987', label: 'Surrender'},
  {code: '988', label: 'Plan of Treatment Period'},
  {
    code: '989',
    label: 'Prior Hospitalization Date(s) Related to Current Service(s)',
  },
  {code: '990', label: 'Original Name Change'},
  {code: '992', label: 'Date Requested'},
  {
    code: '993',
    label: 'Request for Quotation',
    description:
      'The reference date associated with a specifically numbered request for quotation',
  },
  {
    code: '994',
    label: 'Quote',
    description: 'The date on which a (price) quote was rendered',
  },
  {code: '995', label: 'Recorded Date'},
  {
    code: '996',
    label: 'Required Delivery',
    description:
      'A date on which or before, ordered goods or services must be delivered',
  },
  {
    code: '997',
    label: 'Quote to be Received By',
    description:
      'A date on which or before a quote must be received at a location specified in an RFQ',
  },
  {code: '998', label: 'Continuation of Pay Start Date'},
  {code: '999', label: 'Document Date'},
  {code: 'AA1', label: 'Estimated Point of Arrival'},
  {code: 'AA2', label: 'Estimated Point of Discharge'},
  {code: 'AA3', label: 'Cancel After, Ex Country'},
  {code: 'AA4', label: 'Cancel After, Ex Factory'},
  {code: 'AA5', label: 'Do Not Ship Before, Ex Country'},
  {code: 'AA6', label: 'Do Not Ship Before, Ex Factory'},
  {code: 'AA7', label: 'Final Scheduled Payment'},
  {code: 'AA8', label: 'Actual Discharge'},
  {code: 'AA9', label: 'Address Period'},
  {code: 'AAA', label: 'Arrival in Country'},
  {code: 'AAB', label: 'Citation'},
  {code: 'AAC', label: 'Suspension Effective'},
  {code: 'AAD', label: 'Crime'},
  {code: 'AAE', label: 'Discharge – Planned'},
  {code: 'AAF', label: 'Draft'},
  {code: 'AAG', label: 'Due Date'},
  {code: 'AAH', label: 'Event'},
  {code: 'AAI', label: 'First Involvement'},
  {code: 'AAJ', label: 'Guarantee Period'},
  {code: 'AAK', label: 'Income Increase Period'},
  {code: 'AAL', label: 'Installment Date'},
  {code: 'AAM', label: 'Last Civilian Flight'},
  {code: 'AAN', label: 'Last Flight'},
  {code: 'AAO', label: 'Last Insurance Medical'},
  {code: 'AAP', label: 'Last Military Flight'},
  {code: 'AAQ', label: 'Last Physical'},
  {code: 'AAR', label: 'License'},
  {code: 'AAS', label: 'Medical Certificate'},
  {code: 'AAT', label: 'Medication'},
  {code: 'AAU', label: 'Net Worth Date'},
  {code: 'AAV', label: 'Next Activity'},
  {code: 'AAW', label: 'Ownership Change'},
  {code: 'AAX', label: 'Ownership Period'},
  {code: 'AAY', label: 'Rate Date'},
  {code: 'AAZ', label: 'Requested Contract'},
  {code: 'AB1', label: 'Requested Offer'},
  {code: 'AB2', label: 'Sales Period'},
  {code: 'AB3', label: 'Tax Year'},
  {code: 'AB4', label: 'Time Period'},
  {code: 'AB5', label: 'Travel'},
  {code: 'AB6', label: 'Treatment End'},
  {code: 'AB7', label: 'Treatment Start'},
  {code: 'AB8', label: 'Trust'},
  {code: 'AB9', label: 'Worst Time to Call'},
  {code: 'ABA', label: 'Registration'},
  {code: 'ABB', label: 'Revoked'},
  {code: 'ABC', label: 'Estimated Date of Birth'},
  {code: 'ABD', label: 'Last Annual Report'},
  {code: 'ABE', label: 'Legal Action Started'},
  {code: 'ABF', label: 'Lien'},
  {code: 'ABG', label: 'Payment Period'},
  {code: 'ABH', label: 'Profit Period'},
  {code: 'ABI', label: 'Registered'},
  {
    code: 'ABK',
    label: 'Consolidated',
    description: 'Date businesses consolidated or merged',
  },
  {code: 'ABL', label: 'Board of Directors Not Authorized As Of'},
  {code: 'ABM', label: 'Board of Directors Incomplete As Of'},
  {code: 'ABN', label: 'Manager Not Registered As Of'},
  {code: 'ABO', label: 'Citizenship Change'},
  {code: 'ABP', label: 'Participation'},
  {code: 'ABQ', label: 'Capitalization'},
  {code: 'ABR', label: 'Registration of Board of Directors'},
  {code: 'ABS', label: 'Ceased Operations'},
  {code: 'ABT', label: 'Satisfied'},
  {code: 'ABU', label: 'Terms Met'},
  {code: 'ABV', label: 'Asset Documentation Expiration'},
  {code: 'ABW', label: 'Credit Documentation Expiration'},
  {code: 'ABX', label: 'Income Documentation Expiration'},
  {code: 'ABY', label: 'Product Held Until'},
  {code: 'ACA', label: 'Immigration Date'},
  {code: 'ACB', label: 'Estimated Immigration Date'},
  {code: 'ACC', label: 'Current Disability Period Start'},
  {code: 'ACD', label: 'Current Disability Period End'},
  {code: 'ACE', label: 'Current Disability Period Last Day Worked'},
  {code: 'ACF', label: 'Benefit Type Gross Weekly Amount Effective'},
  {code: 'ACG', label: 'Benefit Type Net Weekly Amount Effective'},
  {code: 'ACH', label: 'Benefit Type Period Start'},
  {code: 'ACI', label: 'Benefit Type Period End'},
  {code: 'ACJ', label: 'Benefit Debit Start'},
  {code: 'ACK', label: 'Acknowledgment'},
  {code: 'ACL', label: 'Benefit Debit End'},
  {code: 'ACM', label: 'Benefit Credit Start'},
  {code: 'ACN', label: 'Benefit Credit End'},
  {code: 'ACO', label: 'Benefit Transfer Start'},
  {code: 'ACP', label: 'Benefit Transfer End'},
  {code: 'ACQ', label: 'Wage Effective'},
  {code: 'ACR', label: 'Full Denial Effective'},
  {code: 'ACS', label: 'Full Denial Rescission'},
  {code: 'ACT', label: 'Payment Issue'},
  {code: 'ACU', label: 'Payment Period Start'},
  {code: 'ACV', label: 'Payment Period End'},
  {code: 'ACW', label: 'Employer Reported Injury To Claim Administrator'},
  {code: 'ACX', label: 'Survey Year'},
  {code: 'ACZ', label: 'Controvert Date'},
  {code: 'ADA', label: 'Billed Through'},
  {code: 'ADB', label: 'Business Control Change'},
  {code: 'ADC', label: 'Court Registration'},
  {
    code: 'ADD',
    label: 'Annual Report Due',
    description: 'Date annual report will be due with state',
  },
  {code: 'ADE', label: 'Claim Notification Received'},
  {code: 'ADF', label: 'Conversion Privilege End'},
  {code: 'ADG', label: 'Dividend Applied'},
  {code: 'ADH', label: 'In-force'},
  {code: 'ADI', label: 'Paid-up'},
  {code: 'ADJ', label: 'Premium Change'},
  {code: 'ADK', label: 'Policy Effective on or before'},
  {code: 'ADL', label: 'Asset and Liability Schedule'},
  {
    code: 'ADM',
    label: 'Annual Report Mailed',
    description:
      'Date annual report was mailed when last filed with state corporate authorities',
  },
  {code: 'ADN', label: 'Policy Effective on or after'},
  {
    code: 'ADR',
    label: 'Annual Report Filed',
    description:
      'Date annual report last filed with state corporate authorities',
  },
  {code: 'ADS', label: 'Audit Period After Split Date'},
  {code: 'ADT', label: 'Audit Period Prior to Split Date'},
  {code: 'ADU', label: 'Exposure Source Period'},
  {code: 'ADV', label: 'Subcontractor Period of Hire'},
  {code: 'ADW', label: 'Divorce'},
  {code: 'ADX', label: 'Power of Attorney'},
  {code: 'ADY', label: 'Uniform Gifts to Minors Account Established'},
  {code: 'AEA', label: 'Medicare Part A Eligibility Begin Date'},
  {code: 'AEB', label: 'Medicare Part A Eligibility End Date'},
  {code: 'AEC', label: 'Medicare Part A Coverage Effective Date'},
  {code: 'AED', label: 'Medicare Part A Termination Date'},
  {code: 'AEE', label: 'Medicare Part B Eligibility Begin Date'},
  {code: 'AEF', label: 'Medicare Part B Eligibility End Date'},
  {code: 'AEG', label: 'Medicare Part B Coverage Effective Date'},
  {code: 'AEH', label: 'Medicare Part B Termination Date'},
  {code: 'AEI', label: 'Loading Period'},
  {
    code: 'AEK',
    label: 'Date on which Assets Judged Insufficient to Pay Creditors',
  },
  {code: 'AEL', label: 'Employees Temporarily Laid Off Begin Period'},
  {code: 'AEM', label: 'Employees Temporarily Laid Off End Period'},
  {code: 'AEN', label: 'First Published'},
  {code: 'AEO', label: 'Forecast Period Start'},
  {code: 'AEP', label: 'Forecast Period End'},
  {code: 'AEQ', label: 'Investigation Start'},
  {code: 'AER', label: 'Investigation End'},
  {code: 'AES', label: 'Last Published'},
  {code: 'AET', label: 'Latest Balance Sheet'},
  {code: 'AEU', label: 'Share Price'},
  {code: 'AEV', label: 'Stop Distribution'},
  {
    code: 'AEW',
    label: 'Maximum Credit Date',
    description: 'Date of highest credit provided to entity by a vendor',
  },
  {code: 'AEX', label: 'Founding Date'},
  {code: 'AEY', label: 'Repayment Plan Start Date'},
  {code: 'AFA', label: 'Medicare Part D Eligibility Begin Date'},
  {code: 'AFB', label: 'Medicare Part D Eligibility End Date'},
  {code: 'AFC', label: 'Medicare Part D Coverage Effective Date'},
  {code: 'AFD', label: 'Medicare Part D Termination Date'},
  {
    code: 'ARD',
    label: 'Annual Report Delinquency',
    description:
      'Date annual report will be delinquent with state corporate authorities',
  },
  {code: 'AWH', label: 'Withheld Date'},
  {code: 'BAA', label: 'Compliance Audit'},
  {code: 'BAB', label: 'Contractor Safety Performance Evaluation'},
  {code: 'BAC', label: 'Contractor Safety Procedures Review'},
  {code: 'BAD', label: 'Date of Equipment Inspection'},
  {code: 'BAE', label: 'Date of Safety Inspection'},
  {code: 'BAF', label: 'Employees Participation Plan Review'},
  {
    code: 'BAG',
    label: 'Expected Completion of Changes Resulting from Compliance Audit',
  },
  {
    code: 'BAH',
    label:
      'Expected Completion of Changes Resulting from Process Hazard Analysis',
  },
  {
    code: 'BAI',
    label: 'Expected Completion of Changes Resulting from Hazard Review',
  },
  {code: 'BAJ', label: 'Hazard Review Completion'},
  {code: 'BAK', label: 'Hot Work Permit Procedures Review'},
  {code: 'BAL', label: 'Investigation'},
  {code: 'BAM', label: 'Maintenance Procedures Review'},
  {code: 'BAN', label: 'Management of Change Procedures Review'},
  {code: 'BAO', label: 'Operating Procedures Review'},
  {code: 'BAP', label: 'Safety Information Review'},
  {code: 'BAQ', label: 'Training'},
  {code: 'BAR', label: 'Training Program Review'},
  {code: 'BED', label: 'Billback End Date'},
  {code: 'BEE', label: 'Program Performance End Date'},
  {code: 'BES', label: 'Program Performance Start Date'},
  {code: 'BGP', label: 'Beginning of Grace Period'},
  {
    code: 'BIA',
    label: 'Billing Activities',
    description:
      'Date of earliest billing action through date of latest billing action',
  },
  {code: 'BIP', label: 'Beginning of Interest Paid After Claim'},
  {code: 'BSD', label: 'Billback Start Date'},
  {code: 'CAD', label: 'Changed Accounting Date'},
  {code: 'CCR', label: 'Customs Cargo Release'},
  {code: 'CDD', label: 'Contract Definitization Date'},
  {code: 'CDT', label: 'Maintenance Comment'},
  {code: 'CEA', label: 'Formation'},
  {code: 'CEB', label: 'Continuance'},
  {code: 'CEC', label: 'Merger'},
  {code: 'CED', label: 'Year Due'},
  {code: 'CEE', label: 'Next Annual Meeting'},
  {code: 'CEF', label: 'End of Last Fiscal Year'},
  {code: 'CEH', label: 'Year Beginning'},
  {code: 'CEJ', label: 'Started Doing Business'},
  {code: 'CEK', label: 'Sworn and Subscribed'},
  {code: 'CEL', label: 'Calendar Year'},
  {code: 'CEM', label: 'Asset'},
  {code: 'CEN', label: 'Inactivity'},
  {code: 'CEO', label: 'High Capital Year'},
  {code: 'CLO', label: 'Closing Date of First Balance Sheet'},
  {code: 'CLU', label: 'Closed Until'},
  {code: 'COM', label: 'Compliance'},
  {code: 'CON', label: 'Converted into Holding Company'},
  {
    code: 'COS',
    label: 'Care of Supplies in Storage Inspection Date',
    description:
      'Identifies the date that items in storage were last inspected',
  },
  {code: 'CRV', label: 'Claim Revised'},
  {code: 'CUR', label: 'Current List'},
  {code: 'DAF', label: 'Account Frozen'},
  {code: 'DDO', label: 'Declaration'},
  {code: 'DEE', label: 'Deed Not Available'},
  {code: 'DEL', label: 'Delete'},
  {code: 'DET', label: 'Detrimental Information Received'},
  {code: 'DFF', label: 'Deferral'},
  {code: 'DFS', label: 'Departure From Specification'},
  {code: 'DIL', label: 'Deed In Lieu (DIL) Approved'},
  {code: 'DIP', label: 'Delayed Interest Paid Through'},
  {code: 'DIS', label: 'Disposition'},
  {code: 'DLC', label: 'Date of Last Contact'},
  {code: 'DOA', label: 'Date of Abandonment'},
  {code: 'DOD', label: 'Date of Delinquency'},
  {code: 'DOI', label: 'Delivery Order Issued'},
  {code: 'DOR', label: 'Repossession'},
  {code: 'DSP', label: 'Disposal'},
  {code: 'DTC', label: 'Deed and Title Received'},
  {code: 'DTD', label: 'Technical Data Supply By'},
  {code: 'DTQ', label: 'Deed and Title Requested'},
  {code: 'E01', label: 'Tenure Decision'},
  {code: 'E02', label: 'Most Recent Position Change'},
  {code: 'E03', label: 'Fee Payment'},
  {code: 'E04', label: 'Start Date for Continuous Employment'},
  {code: 'E05', label: 'Start Date for Current Position'},
  {code: 'E06', label: 'Start Date for Original Position'},
  {code: 'E07', label: 'Fiscal Year'},
  {code: 'ECD', label: 'Estimated Construction Date'},
  {code: 'ECF', label: 'Estimated Completion – First Prior Month'},
  {code: 'ECS', label: 'Estimated Completion – Second Prior Month'},
  {code: 'ECT', label: 'Estimated Completion – Third Prior Month'},
  {code: 'EDA', label: 'Affirmed'},
  {code: 'EDB', label: 'Auction'},
  {code: 'EDC', label: 'Authorized'},
  {code: 'EDD', label: 'Contribution'},
  {code: 'EDE', label: 'Executed'},
  {code: 'EDF', label: 'Forgiven'},
  {code: 'EDG', label: 'Presented'},
  {code: 'EDH', label: 'Legislative Session'},
  {code: 'EDI', label: 'Organized'},
  {code: 'EDJ', label: 'Pledged'},
  {code: 'EDK', label: 'Primary Election'},
  {code: 'EDL', label: 'Qualified'},
  {code: 'EDM', label: 'Refunded'},
  {code: 'EDN', label: 'Rescinded'},
  {code: 'EDO', label: 'Restructured From'},
  {code: 'EDP', label: 'Vote'},
  {code: 'EKD', label: 'Employer Knowledge of the Disability'},
  {code: 'EPP', label: 'Estimate Preparation'},
  {code: 'ESC', label: 'Estimate Comment'},
  {code: 'ESF', label: 'Estimated Start – First Prior Month'},
  {code: 'ESS', label: 'Estimated Start – Second Prior Month'},
  {code: 'EST', label: 'Estimated Start – Third Prior Month'},
  {code: 'ETP', label: 'Earliest Filing Period'},
  {code: 'EXO', label: 'Exposure'},
  {code: 'EXP', label: 'Export'},
  {
    code: 'FAC',
    label: 'Facility Latest Billing Action',
    description:
      'Date of most recent billable service provided during a spell of illness',
  },
  {
    code: 'FEB',
    label: 'Facility Earliest Billing Action',
    description:
      'Date of the earliest billable service provided during a spell of illness',
  },
  {code: 'FFI', label: 'Financial Information'},
  {code: 'FFO', label: 'First Order'},
  {code: 'FIA', label: 'Final Interest Accrual'},
  {code: 'FPE', label: 'Funding Period – End'},
  {code: 'FPS', label: 'Funding Period – Start'},
  {code: 'FSE', label: 'Free Service Call End Date'},
  {code: 'FSS', label: 'Free Service Call Start Date'},
  {code: 'GRD', label: 'Graduated'},
  {
    code: 'HHD',
    label: 'Home Health Date of Earliest Billable Action',
    description:
      'Date of the earliest billable service provided by a home health provider during an episode',
  },
  {
    code: 'HHE',
    label: 'Home Health Episode',
    description:
      'Dates during which a patient is under the care of a home health agency',
  },
  {
    code: 'HHL',
    label: 'Home Health Date of Latest Billable Action',
    description:
      'Date of the most recent billable service provided by a home health provider during an episode',
  },
  {code: 'ICF', label: 'Converted to Electronic Date'},
  {code: 'IDG', label: 'Insolvency Discharge Granted'},
  {code: 'IFH', label: 'Initial Federal Housing Authority Claim Payment'},
  {code: 'III', label: 'Incorporation'},
  {code: 'ILU', label: 'Image Last Update Date'},
  {code: 'IMB', label: 'Imbalance Period'},
  {code: 'IMP', label: 'Import'},
  {code: 'INC', label: 'Incident'},
  {code: 'INT', label: 'Inactive Until'},
  {code: 'IPS', label: 'Interest on Presale Start'},
  {code: 'IVA', label: 'Initial Veterans Administration Claim Payment'},
  {code: 'KEV', label: 'Key Event Fiscal Year'},
  {code: 'KEW', label: 'Key Event Calendar Year'},
  {code: 'LAM', label: 'Last Annual Meeting'},
  {code: 'LAS', label: 'Last Check for Balance Sheet Update'},
  {
    code: 'LCC',
    label: 'Last Capital Change',
    description: 'Date capital stock was last modified',
  },
  {code: 'LEA', label: 'Letter of Agreement'},
  {code: 'LEL', label: 'Letter of Liability'},
  {code: 'LIQ', label: 'Liquidation'},
  {code: 'LLP', label: 'Low Period'},
  {code: 'LOG', label: 'Equipment Log Entry'},
  {code: 'LPC', label: 'List Price Change'},
  {code: 'LSC', label: 'Legal Structure Change'},
  {code: 'LSD', label: 'Last Submission Date'},
  {code: 'LTP', label: 'Latest Filing Period'},
  {code: 'MRR', label: 'Meter Reading'},
  {code: 'MSD', label: 'Latest Material Safety Data Sheet Date'},
  {code: 'NAM', label: 'Present Name'},
  {code: 'NFD', label: 'Negotiated Finish'},
  {code: 'NOD', label: 'Notice of Delinquency (NOD) Received'},
  {code: 'NRG', label: 'Not Registered'},
  {code: 'NSD', label: 'Negotiated Start'},
  {
    code: 'OCD',
    label: 'Organic Certification Date',
    description:
      'The date that the organic certification was issued by the organic certification agency',
  },
  {code: 'ORG', label: 'Original List'},
  {code: 'PBC', label: 'Present Control'},
  {code: 'PCP', label: 'Primary Coverage Claim Paid'},
  {code: 'PCS', label: 'Primary Coverage Claim Submission'},
  {code: 'PCT', label: 'Price Changes Allowed From Date'},
  {code: 'PCU', label: 'Price Changes Allowed To Date'},
  {code: 'PDE', label: 'Partial Denial Effective'},
  {code: 'PDR', label: 'Partial Denial Rescission'},
  {code: 'PDS', label: 'Correct Program Start Date'},
  {code: 'PDT', label: 'Correct Program End Date'},
  {code: 'PDU', label: 'Correct Contract Start Date'},
  {code: 'PDV', label: 'Privilege Details Verification'},
  {code: 'PDW', label: 'Correct Contract End Date'},
  {code: 'PED', label: 'Program End Date'},
  {code: 'PLS', label: 'Present Legal Structure'},
  {code: 'PPC', label: 'Pool Policy Claim Submission'},
  {
    code: 'PPD',
    label: 'Post Paid Date',
    description: 'The last date a tax authority posted tax payments',
  },
  {code: 'PPP', label: 'Peak Period'},
  {code: 'PRD', label: 'Previously Reported Date of Birth'},
  {code: 'PRR', label: 'Presented to Receivers'},
  {code: 'PSA', label: 'Property Sale Approved'},
  {code: 'PSC', label: 'Property Sale Closed'},
  {code: 'PSD', label: 'Program Start Date'},
  {code: 'PSF', label: 'Property Sale Confirmation'},
  {code: 'PTD', label: 'Paid To Date'},
  {code: 'PTO', label: 'Plan to Obsolete'},
  {code: 'PUD', label: 'Pick-up Date'},
  {code: 'RAP', label: 'Receiver Appointed'},
  {
    code: 'REM',
    label: 'Remanufacture Date',
    description:
      'The date a product was refurbished by renovating and reassembling its components',
  },
  {code: 'RES', label: 'Resigned'},
  {code: 'RFD', label: 'Requested Finish'},
  {code: 'RFF', label: 'Recovery Finish'},
  {code: 'RFO', label: 'Referred From'},
  {code: 'RNT', label: 'Rent Survey'},
  {code: 'RRM', label: 'Received in the Mail'},
  {code: 'RRT', label: 'Revocation'},
  {code: 'RSD', label: 'Requested Start'},
  {code: 'RSS', label: 'Recovery Start'},
  {code: 'RTO', label: 'Referred To'},
  {code: 'SCV', label: 'Social Security Claims Verification'},
  {code: 'SDD', label: 'Sole Directorship Date'},
  {code: 'SFH', label: 'Subsequent Federal Housing Authority Claim Payment'},
  {code: 'SPD', label: 'Initial Support Date'},
  {code: 'STN', label: 'Transition'},
  {code: 'SVA', label: 'Subsequent Veterans Administration Claim Payment'},
  {code: 'TSR', label: 'Trade Style Registered'},
  {code: 'TSS', label: 'Trial Started'},
  {code: 'TST', label: 'Trial Set'},
  {code: 'VAT', label: 'Value Added Tax (VAT) Claims Verification'},
  {code: 'VLU', label: 'Valid Until'},
  {code: 'W01', label: 'Sample Collected'},
  {code: 'W02', label: 'Status Change'},
  {code: 'W03', label: 'Construction Start'},
  {code: 'W05', label: 'Recompletion'},
  {code: 'W06', label: 'Last Logged'},
  {code: 'W07', label: 'Well Log Run'},
  {code: 'W08', label: 'Surface Casing Authority Approval'},
  {code: 'W09', label: 'Reached Total Depth'},
  {code: 'W10', label: 'Spacing Order Unit Assigned'},
  {code: 'W11', label: 'Rig Arrival'},
  {code: 'W12', label: 'Location Exception Order Number Assigned'},
  {code: 'W13', label: 'Sidetracked Wellbore'},
  {code: 'WAA', label: 'Time Employee Began Work'},
  {code: 'WAY', label: 'Waybill'},
  {
    code: 'XX1',
    label: 'Order Day',
    description: 'Day of week when an order may be placed',
  },
  {
    code: 'XX2',
    label: 'Delivery Day',
    description: 'Day of week when an order delivery will occur',
  },
  {
    code: 'XX3',
    label: 'Order Cut-Off Time',
    description: 'Latest time-of-day when an order may be placed',
  },
  {code: 'YXX', label: 'Programmed Fiscal Year'},
  {code: 'YXY', label: 'Programmed Calendar Year'},
  {code: 'ZZZ', label: 'Mutually Defined'},
];
